import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useState } from 'react';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import UploadIcon from '@mui/icons-material/Upload';
import MUIModal from '../../components/MuiModal';
import UploadImage from './UploadImage';
import { useEffect } from 'react';
import { apiConstants, post } from '../../common/constants';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import UpdateStatus from './UpdateStatus';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import _ from 'lodash';
import StatusLog from './SatatusLog';
import { formatCurrency, formatDate } from '../../common/util';
import { convertDate } from '../../helpers/UtilityHelper';
import { Link } from 'react-router-dom';
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls';
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from "notistack";
import Loader from '../../common/Loader';
import ViewImags from './ViewImags';
import RHFMultipleSelectField from '../../common/hook-form/RHFMultipleSelectField';
import OrderRowActions from '../../common/OrderRowActions';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    NEW:                    { bg: '#dbeafe', fg: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6', label: 'New' },
    DISPUTED_BY_COURIER:    { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca', dot: '#ef4444', label: 'Disputed by Courier' },
    DISPUTED_BY_MERCHANT:   { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca', dot: '#ef4444', label: 'Disputed by Merchant' },
    ACCEPTED_BY_COURIER:    { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e', label: 'Accepted by Courier' },
    ACCEPTED_BY_MERCHANT:   { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e', label: 'Accepted by Merchant' },
    AUTO_ACCEPTED_BY_SYSTEM:{ bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e', label: 'System Accepted' },
    REJECTED_PROSHIP:       { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', dot: '#ef4444', label: 'Rejected Proship' },
    ACCEPTED_PROSHIP:       { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e', label: 'Accepted Proship' },
};

function DiscrepancyStatusBadge({ status }) {
    if (!status) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    const cfg = STATUS_CONFIG[status] || { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb', dot: '#9ca3af', label: status };
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            px: '9px', py: '4px', borderRadius: '20px',
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.2px',
            bgcolor: cfg.bg, color: cfg.fg, border: `1px solid ${cfg.border}`,
            whiteSpace: 'normal', maxWidth: '100%',
        }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
            {cfg.label}
        </Box>
    );
}

// Weight / amount chip row
function WeightRow({ weight, amount, slabs, highlight }) {
    const fmtKg = (v) => v != null ? `${(v / 1000).toFixed(2)} kg` : 'NA';
    const fmtSlab = (s) => s ? `${(s.startWeight / 1000).toFixed(2)}–${(s.endWeight / 1000).toFixed(2)} kg` : 'NA';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: 0.5 }}>
            <Box component="span" sx={{
                display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px', width: 'fit-content',
                fontSize: '11px', fontWeight: 700,
                bgcolor: highlight ? '#fef2f2' : '#f0fdf4',
                color: highlight ? '#dc2626' : '#15803d',
                border: `1px solid ${highlight ? '#fecaca' : '#bbf7d0'}`,
            }}>
                {fmtKg(weight)}
            </Box>
            <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{amount ? formatCurrency(amount) : 'NA'}</Box>
            <Box sx={{ fontSize: '10px', color: '#9ca3af' }}>{fmtSlab(slabs)}</Box>
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

function AllDiscrepancy({ merchantName, merchantId, location = 'WD' }) {
    const methods = useForm({
        defaultValues: { status: "", merchant: "", awb: "", fromDate: "", toDate: "" }
    });
    const { enqueueSnackbar } = useSnackbar();
    const { handleSubmit, reset } = methods;
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({});
    const [sizePerPage, setSizePerPage] = useState(10);
    const [reload, setReload] = useState(0);
    const [open, setOpen] = useState(false);
    const [awb, setAwb] = useState();
    const [Status, setStatus] = useState();
    const [dispute, setDispute] = useState(false);
    const [agree, setAgree] = useState(false);
    const [log, setLog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);
    const [imageData, setImageDate] = useState([]);
    const [imageType, setImageType] = useState('');

    const filter2 = { "andfilter": [{ "field": "is_deleted", "operator": "eq", "value": '0' }], "offset": 0, "limit": 1000, "skipCount": true, "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType" };
    const { data: allCourier } = useGetAllCourierPartnersCachedQuery(filter2);
    const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();

    const statusOptions = [
        { label: "New", value: 'NEW' },
        { label: "DISPUTED BY COURIER", value: 'DISPUTED_BY_COURIER' },
        { label: "DISPUTED BY MERCHANT", value: 'DISPUTED_BY_MERCHANT' },
        { label: "ACCEPTED BY COURIER", value: 'ACCEPTED_BY_COURIER' },
        { label: "ACCEPTED BY MERCHANT", value: 'ACCEPTED_BY_MERCHANT' },
        { label: "REJECTED PROSHIP", value: 'REJECTED_PROSHIP' },
        { label: "ACCEPTED PROSHIP", value: 'ACCEPTED_PROSHIP' },
        { label: "AUTO ACCEPTED BY SYSTEM", value: 'AUTO_ACCEPTED_BY_SYSTEM' },
    ];

    useEffect(() => {
        if (filters.merchantId) {
            setLoading(true);
            post(apiConstants.WEIGHT_DISCREPANCY, { ...filters, skip: page * sizePerPage, limit: sizePerPage, excludes: "orderId,clientOrderReference,couriorProviderName,proshipDimensions,courierDimensions,merchantPricing,courierPricing,merchantPricingRTO,courierPricingRTO" })
                .then((res) => { setLoading(false); setData(res?.data?.responseObj); })
                .catch((err) => { console.log(err); setLoading(false); });
        }
    }, [reload, filters, sizePerPage, page]);

    useEffect(() => {
        console.log(filters, 'here2  ');
        if (merchantId && location !== 'WD') {
            setLoading(true);
            post(apiConstants.WEIGHT_DISCREPANCY, { ...filters, skip: page * sizePerPage, limit: sizePerPage, merchantId, excludes: "orderId,clientOrderReference,couriorProviderName,proshipDimensions,courierDimensions,merchantPricing,courierPricing,merchantPricingRTO,courierPricingRTO" })
                .then((res) => { setLoading(false); setData(res?.data?.responseObj); })
                .catch((err) => { console.log(err); setLoading(false); });
        }
    }, [reload, merchantId, sizePerPage, page, filters]);

    const downloadReport = (fileName, filters) => {
        setLoading(true);
        const dlFilters = { ...filters, skip: 0, limit: 100000, merchantId: location === 'WD' ? methods.getValues('merchant') : merchantId, statusIn: methods.getValues('status') === 'ALL' ? '' : methods.getValues('status'), awbNumber: methods.getValues('awb'), createdDateLT: methods.getValues('toDate') ? convertDate(methods.getValues('toDate')) : "", createdDateGT: methods.getValues('fromDate') ? convertDate(methods.getValues('fromDate')) : "" };
        post(`${apiConstants.WEIGHT_DISCREPANCY}?download=true`, dlFilters).then((res) => {
            if (res?.data?.reportMeta?.s3Link?.signedLink) {
                // window.open() called here (inside an async .then, after the
                // network round-trip) loses the "user gesture" context most
                // browsers require for popups, so it gets silently blocked —
                // no error, nothing visibly happens. A synthetic <a target=
                // "_blank"> click is treated as normal navigation, not a
                // popup, so it survives the async boundary reliably.
                const link = document.createElement("a");
                link.href = res.data.reportMeta.s3Link.signedLink;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else if (res?.data?.status === 'ERROR') {
                // Backend failed generating/uploading the CSV. Its exception.message
                // is a raw Java stack-trace/object dump (sometimes hundreds of
                // characters long) — never show that to a user. Show one short,
                // human sentence instead, with the tracking ID so support can look
                // up the exact failure server-side if it keeps happening.
                const ref = res?.data?.trackingId ? ` (Ref: ${res.data.trackingId})` : '';
                enqueueSnackbar(`Couldn't generate the report. Please try again in a moment.${ref}`, { variant: 'error' });
            } else if (typeof res?.data === 'string' && res.data.length > 0) {
                // Defensive fallback only for an endpoint that genuinely returns raw
                // CSV text (this endpoint currently never does — it always uploads
                // to S3 and returns the signedLink above).
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = encodeURI(url);
                link.setAttribute("download", `${fileName}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                enqueueSnackbar('No report was generated. Please try again.', { variant: 'error' });
            }
        }).catch((err) => { console.log(err); enqueueSnackbar("Something went wrong", { variant: "error" }); })
          .finally(() => setLoading(false));
    };

    const onSubmit = (data) => {
        const merchantIdValue = location === 'WD' ? data.merchant : merchantId;
        setFilters({ merchantId: merchantIdValue, statusIn: data.status === 'ALL' ? '' : data.status, awbNumber: data.awb, createdDateLT: data.toDate ? convertDate(data.toDate) : "", createdDateGT: data.fromDate ? convertDate(data.fromDate) : "" });
    };

    const handleImgOpen = (data, type) => { setImageOpen(true); setImageDate(data); setImageType(type); };
    const handleOpen = (id) => { setOpen(true); setAwb(id); };
    const handleOpenDispute = (id) => { setDispute(true); setAwb(id); setStatus('DISPUTE'); };
    const handleOpenAgree = (id) => { setAgree(true); setAwb(id); setStatus('ACCEPT'); };
    const handleClose = () => { setOpen(false); setAgree(false); setDispute(false); setLog(false); setImageOpen(false); setImageType(''); };
    const handleOpenstatus = (id) => { setLog(true); setAwb(id); };

    const merchatId = methods.watch('merchant');
    const selectedMerchant = allMerchants.find(m => m.id === merchatId);
    const tooltipMessage = `Download weight discrepancy data for ${!merchatId ? 'All merchants' : selectedMerchant?.name || 'selected merchant'}`;

    const columns = [
        {
            field: 'merchantName',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantName || '—'}</CellLine>,
        },
        {
            field: 'Current Status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <DiscrepancyStatusBadge status={row.status} />
                    {row?.statusLog?.length > 0 && (
                        <Box sx={{ fontSize: '10px', color: '#9ca3af' }}>
                            {formatDate(row.statusLog[row.statusLog.length - 1]?.date, { format: "dd MMM, HH:mm" })}
                        </Box>
                    )}
                    {/* Persisted only on WDs created after weight-v2 — hidden on legacy rows */}
                    {row?.shipmentState && (
                        <Box sx={{ fontSize: '10px', fontWeight: 600, color: row.shipmentState === 'DELIVERED' ? '#15803d' : '#b45309' }}>
                            {row.shipmentState === 'DELIVERED' ? 'Delivered' : 'In Transit'}
                        </Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'latestRemark',
            headerName: 'Remark', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine muted ellipsis>{row?.latestRemark || '—'}</CellLine>,
        },
        {
            field: 'awb',
            headerName: 'AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component={Link} to={`/order-detail/${row.awbNumber}`}
                        sx={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd', pb: '1px', '&:hover': { color: '#1e40af', borderBottomStyle: 'solid' } }}>
                        {row?.awbNumber}
                    </Box>
                    <CopyIcon text={row?.awbNumber} />
                </Box>
            ),
        },
        {
            field: 'shippingdetails',
            headerName: 'Courier', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => {
                const courier = allCourier?.find(item => item.prozoId === row.couriorProvider);
                return (
                    <CellStack>
                        <CellLine bold>{courier?.parent || '—'}</CellLine>
                        {courier?.cpAccountCode && <CellLine muted>{courier.cpAccountCode}</CellLine>}
                    </CellStack>
                );
            },
        },
        {
            field: 'merchantoriginal',
            headerName: 'Merchant Original', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 160,
            renderCell: ({ row }) => (
                <WeightRow
                    weight={row?.originalWeight}
                    amount={row?.originalAmount}
                    slabs={row?.weightSlabsMerchant?.originalSlab}
                    highlight={false}
                />
            ),
        },
        {
            field: 'merchantApplied',
            headerName: 'Merchant Applied', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 160,
            renderCell: ({ row }) => (
                <WeightRow
                    weight={row?.finalChargedWeight}
                    amount={row?.finalAmountCharged}
                    slabs={row?.weightSlabsMerchant?.finalAppliedSlab}
                    highlight={true}
                />
            ),
        },
        {
            field: 'courieroriginal',
            headerName: 'Courier Original', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 160,
            renderCell: ({ row }) => (
                <WeightRow
                    weight={row?.originalWeight}
                    amount={row?.originalAmountCourier}
                    slabs={row?.weightSlabsCourier?.originalSlab}
                    highlight={false}
                />
            ),
        },
        {
            field: 'courierApplied',
            headerName: 'Courier Applied', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 160,
            renderCell: ({ row }) => (
                <Box>
                    <WeightRow
                        weight={row?.finalChargedWeight}
                        amount={row?.finalAmountChargedCourier}
                        slabs={row?.weightSlabsCourier?.finalAppliedSlab}
                        highlight={true}
                    />
                    {/* Courier-measured split — persisted only on WDs created after weight-v2 */}
                    {(row?.courierDeadWeightGm != null || row?.courierVolWeightGm != null) && (
                        <Box sx={{ fontSize: '10px', color: '#6b7280', pb: 0.5 }}>
                            {row?.courierDeadWeightGm != null && (
                                <Box><DataLabel>Dead</DataLabel>{(row.courierDeadWeightGm / 1000).toFixed(2)} kg</Box>
                            )}
                            {row?.courierVolWeightGm != null && (
                                <Box><DataLabel>Vol</DataLabel>{(row.courierVolWeightGm / 1000).toFixed(2)} kg</Box>
                            )}
                        </Box>
                    )}
                </Box>
            ),
        },
        {
            field: 'createdDate',
            headerName: 'Created', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine muted>{row?.createdDate ? formatDate(row.createdDate, { format: "dd MMM, HH:mm" }) : '—'}</CellLine>
                    {row?.createdByUser && <CellLine muted>{row.createdByUser}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'images',
            headerName: 'Images', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => {
                const actions = [
                    ...(row?.courierImagesS3Link ? [{ key: 'courier', label: 'View courier images', icon: <ImageOutlinedIcon fontSize="small" />, onClick: () => handleImgOpen(row, 'courier') }] : []),
                    ...(row?.merchantImagesS3Link ? [{ key: 'merchant', label: 'View merchant images', icon: <ImageOutlinedIcon fontSize="small" />, onClick: () => handleImgOpen(row, 'merchant'), dividerBefore: row?.courierImagesS3Link ? true : false }] : []),
                ];
                if (!actions.length) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return <OrderRowActions actions={actions} />;
            },
        },
        {
            field: 'action',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => {
                const isResolved = _.intersection(['AUTO_ACCEPTED_BY_SYSTEM', 'ACCEPTED_BY_MERCHANT', 'ACCEPTED_BY_COURIER'], [row?.status]).length > 0;
                const actions = [
                    { key: 'log', label: 'View status log', icon: <LocalLibraryIcon fontSize="small" />, onClick: () => handleOpenstatus(row) },
                    { key: 'upload', label: 'Upload image', icon: <UploadIcon fontSize="small" />, onClick: () => handleOpen(row?.awbNumber), dividerBefore: true },
                    ...(!isResolved ? [
                        { key: 'dispute', label: 'Raise dispute', icon: <SmsFailedIcon fontSize="small" />, onClick: () => handleOpenDispute(row?.awbNumber), color: 'error' },
                        { key: 'accept', label: 'Accept', icon: <CheckCircleIcon fontSize="small" />, onClick: () => handleOpenAgree(row?.awbNumber), color: 'success' },
                    ] : []),
                ];
                return <OrderRowActions actions={actions} />;
            },
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <PageTitle>Weight Discrepancies{merchantName ? ` / ${merchantName}` : ''}</PageTitle>

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Search & Filter</Typography>
                        {location === 'WD' && <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>Select a merchant to view discrepancy details</Typography>}
                    </Box>
                    <Button
                        startIcon={<DownloadSharpIcon fontSize="small" />}
                        onClick={() => downloadReport('report', filters)}
                        variant="contained"
                        size="small"
                        disableElevation
                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}
                    >
                        Download
                    </Button>
                </Box>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            {location === 'WD' && (
                                <FilterField>
                                    <FieldLabel>Merchant</FieldLabel>
                                    <RHFMultipleSelectField
                                        width='100%' name="merchant" search={true} outputFormat='string'
                                        options={[{ label: 'All', value: 'ALL' }, ..._.orderBy(allMerchants, 'name').map(m => ({ label: m.name, value: m.id }))]}
                                        label=""
                                        placeholder="Select Merchant"
                                    />
                                </FilterField>
                            )}

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Status</FieldLabel>
                                <RHFSelectField name='status' label='' placeholder='Select Status' options={[{ label: 'All', value: 'ALL' }, ...statusOptions]} width='100%' size="small" displayEmpty />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset({ awb: "", merchant: "", status: "", fromDate: "", toDate: "" }); setData([]); setFilters({}); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Info banner ───────────────────────────────────────────── */}
            <Alert severity='info' sx={{ mb: 2, borderRadius: '8px', fontSize: '12px' }}>
                <strong>Tips:</strong> Use <strong>Download</strong> to export with current filter settings. Click <strong>Filter</strong> to view results in the table below. Both work independently.
            </Alert>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data || []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    getRowId={(row) => row?.id}
                />
            </Box>

            <MUIModal open={imageOpen} handleClose={handleClose} maxWidth="sm" children={<ViewImags imageData={imageData} imageType={imageType} />} title={`${imageType === 'courier' ? 'Courier' : 'Merchant'} Images`} />
            <MUIModal open={open} handleClose={handleClose} children={<UploadImage id={awb} setReload={setReload} reload={reload} setOpen={setOpen} />}
                title={"Upload Image"} child={<Typography variant="body2" color='red'>File must be in .pdf, .png, .jpg, .jpeg format</Typography>} />
            <MUIModal open={dispute} handleClose={handleClose} children={<UpdateStatus id={awb} setReload={setReload} Status={Status} setOpen={setDispute} />} title={"Raise a dispute"} />
            <MUIModal open={agree} handleClose={handleClose} children={<UpdateStatus id={awb} setReload={setReload} Status={Status} setOpen={setAgree} />} title={"Close Dispute"} />
            <MUIModal open={log} handleClose={handleClose} children={<StatusLog id={awb} />} title={"Status Log"} />
        </>
    );
}

export default AllDiscrepancy;
